export type PublicShellType =
  | 'local-first-landing'
  | 'browse'
  | 'detail'
  | 'checkout'
  | 'account'
  | 'error';

export type HeaderMode = 'full' | 'minimal' | 'hidden';
export type FooterMode = 'full' | 'minimal' | 'none';
export type StickyCtaMode = 'none' | 'book-now-rail' | 'filter-bar';
export type PublicRouteKey =
  | 'root'
  | 'region-home'
  | 'region-calendar'
  | 'region-community'
  | 'locality-home'
  | 'locality-calendar'
  | 'locality-community'
  | 'classes'
  | 'class-detail'
  | 'creators'
  | 'creator-detail'
  | 'search'
  | 'checkout'
  | 'checkout-confirmation'
  | 'account'
  | 'account-enrollments'
  | 'admin-dashboard'
  | 'class-dashboard'
  | 'creator-dashboard'
  | 'venue-dashboard'
  | 'ambassador-dashboard'
  | 'onboarding'
  | 'creator-apply'
  | 'about'
  | 'contact'
  | 'faq'
  | 'not-found'
  | 'home'
  | 'community'
  | 'locations'
  | 'location-detail'
  | 'updates'
  | 'update-detail'
  | 'calendar-submit';

export interface PublicRouteLayout {
  routeKey: PublicRouteKey;
  pathLabel: string;
  shell: PublicShellType;
  headerMode: HeaderMode;
  bottomNavVisible: boolean;
  stickyCta: StickyCtaMode;
  footer: FooterMode;
  description: string;
}

export const PUBLIC_ROUTE_LAYOUTS: Record<PublicRouteKey, PublicRouteLayout> = {
  root: {
    routeKey: 'root',
    pathLabel: '/',
    shell: 'local-first-landing',
    headerMode: 'minimal',
    bottomNavVisible: false,
    stickyCta: 'none',
    footer: 'minimal',
    description:
      'Geography discovery entry that routes returning visitors into their saved market.',
  },
  'region-home': {
    routeKey: 'region-home',
    pathLabel: '/regions/:regionSlug',
    shell: 'local-first-landing',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Region home with local classes, creators, and calendar entry points.',
  },
  'region-calendar': {
    routeKey: 'region-calendar',
    pathLabel: '/regions/:regionSlug/calendar',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'filter-bar',
    footer: 'full',
    description: 'Region agenda with canonical date ownership in the URL.',
  },
  'region-community': {
    routeKey: 'region-community',
    pathLabel: '/regions/:regionSlug/community',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Public region community discussions for local recommendations and planning.',
  },
  'locality-home': {
    routeKey: 'locality-home',
    pathLabel: '/regions/:regionSlug/:localitySlug',
    shell: 'local-first-landing',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Locality home that leads with neighborhood-scale discovery and fallback modules.',
  },
  'locality-calendar': {
    routeKey: 'locality-calendar',
    pathLabel: '/regions/:regionSlug/:localitySlug/calendar',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'filter-bar',
    footer: 'full',
    description: 'Locality agenda and event calendar optimized for one-hand browsing.',
  },
  'locality-community': {
    routeKey: 'locality-community',
    pathLabel: '/regions/:regionSlug/:localitySlug/community',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description:
      'Public locality community discussions with geography-aware entry and return paths.',
  },
  classes: {
    routeKey: 'classes',
    pathLabel: '/classes and /regions/.../classes',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'filter-bar',
    footer: 'full',
    description: 'Classes browse with URL-owned filters and scroll restoration.',
  },
  'class-detail': {
    routeKey: 'class-detail',
    pathLabel: '/classes/:id',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'book-now-rail',
    footer: 'full',
    description: 'Class detail with sticky mobile booking CTA and desktop booking rail.',
  },
  creators: {
    routeKey: 'creators',
    pathLabel: '/creators and /regions/.../creators',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'filter-bar',
    footer: 'full',
    description: 'Creator browse mirroring class discovery interactions.',
  },
  'creator-detail': {
    routeKey: 'creator-detail',
    pathLabel: '/creators/:id',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Creator profile with local context, social proof, and class inventory.',
  },
  search: {
    routeKey: 'search',
    pathLabel: '/search',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'filter-bar',
    footer: 'full',
    description: 'Unified search segmented into class and creator results.',
  },
  checkout: {
    routeKey: 'checkout',
    pathLabel: '/checkout',
    shell: 'checkout',
    headerMode: 'minimal',
    bottomNavVisible: false,
    stickyCta: 'book-now-rail',
    footer: 'minimal',
    description:
      'Reduced checkout shell with progress, support access, and safe-area-aware sticky actions.',
  },
  'checkout-confirmation': {
    routeKey: 'checkout-confirmation',
    pathLabel: '/checkout/confirmation',
    shell: 'checkout',
    headerMode: 'minimal',
    bottomNavVisible: false,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Order confirmation with follow-through actions for calendar add and sharing.',
  },
  account: {
    routeKey: 'account',
    pathLabel: '/account',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Signed-in account overview with upcoming classes, history, and preferences.',
  },
  'account-enrollments': {
    routeKey: 'account-enrollments',
    pathLabel: '/account/enrollments',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Enrollment-focused account tab used by My Classes navigation.',
  },
  'admin-dashboard': {
    routeKey: 'admin-dashboard',
    pathLabel: '/admin',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Admin operations dashboard using the signed-in shell.',
  },
  'class-dashboard': {
    routeKey: 'class-dashboard',
    pathLabel: '/class-dashboard',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Admin class management dashboard using the signed-in shell.',
  },
  'creator-dashboard': {
    routeKey: 'creator-dashboard',
    pathLabel: '/creator-dashboard',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Creator class management dashboard using the signed-in shell.',
  },
  'venue-dashboard': {
    routeKey: 'venue-dashboard',
    pathLabel: '/venue-dashboard',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Venue owner payout dashboard using the signed-in shell.',
  },
  'ambassador-dashboard': {
    routeKey: 'ambassador-dashboard',
    pathLabel: '/ambassador-dashboard',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Ambassador referral payout dashboard using the signed-in shell.',
  },
  onboarding: {
    routeKey: 'onboarding',
    pathLabel: '/onboarding',
    shell: 'account',
    headerMode: 'minimal',
    bottomNavVisible: false,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Focused onboarding flow without discovery chrome.',
  },
  'creator-apply': {
    routeKey: 'creator-apply',
    pathLabel: '/creator/apply',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Create with Kalovate application flow for prospective creators.',
  },
  about: {
    routeKey: 'about',
    pathLabel: '/about',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Brand and trust-building editorial page.',
  },
  contact: {
    routeKey: 'contact',
    pathLabel: '/contact',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Support and contact entry page.',
  },
  faq: {
    routeKey: 'faq',
    pathLabel: '/faq',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Operational FAQ aligned with actual platform behavior.',
  },
  'not-found': {
    routeKey: 'not-found',
    pathLabel: '404',
    shell: 'error',
    headerMode: 'minimal',
    bottomNavVisible: false,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Error shell with recovery links instead of dead ends.',
  },
  home: {
    routeKey: 'home',
    pathLabel: '/home',
    shell: 'account',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'minimal',
    description: 'Signed-in overview with upcoming classes and quick actions.',
  },
  community: {
    routeKey: 'community',
    pathLabel: '/community',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Legacy class-scoped community forum for enrolled learners.',
  },
  locations: {
    routeKey: 'locations',
    pathLabel: '/regions/:regionSlug/:localitySlug/locations',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Locality venue browse — all named class spaces in a neighborhood.',
  },
  'location-detail': {
    routeKey: 'location-detail',
    pathLabel: '/regions/:regionSlug/:localitySlug/locations/:locationSlug',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Venue detail with classes and creators hosted at this space.',
  },
  updates: {
    routeKey: 'updates',
    pathLabel: '/regions/:regionSlug/updates',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Blog and newsletter updates for the region.',
  },
  'update-detail': {
    routeKey: 'update-detail',
    pathLabel: '/updates/:slug',
    shell: 'detail',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Single blog post detail page.',
  },
  'calendar-submit': {
    routeKey: 'calendar-submit',
    pathLabel: '/regions/:regionSlug/calendar/submit',
    shell: 'browse',
    headerMode: 'full',
    bottomNavVisible: true,
    stickyCta: 'none',
    footer: 'full',
    description: 'Public community event submission form.',
  },
};

export const PUBLIC_ROUTE_MAP = [
  PUBLIC_ROUTE_LAYOUTS.root,
  PUBLIC_ROUTE_LAYOUTS['region-home'],
  PUBLIC_ROUTE_LAYOUTS['region-calendar'],
  PUBLIC_ROUTE_LAYOUTS['region-community'],
  PUBLIC_ROUTE_LAYOUTS['locality-home'],
  PUBLIC_ROUTE_LAYOUTS['locality-calendar'],
  PUBLIC_ROUTE_LAYOUTS['locality-community'],
  PUBLIC_ROUTE_LAYOUTS.classes,
  PUBLIC_ROUTE_LAYOUTS['class-detail'],
  PUBLIC_ROUTE_LAYOUTS.locations,
  PUBLIC_ROUTE_LAYOUTS['location-detail'],
  PUBLIC_ROUTE_LAYOUTS.creators,
  PUBLIC_ROUTE_LAYOUTS['creator-detail'],
  PUBLIC_ROUTE_LAYOUTS.search,
  PUBLIC_ROUTE_LAYOUTS.checkout,
  PUBLIC_ROUTE_LAYOUTS['checkout-confirmation'],
  PUBLIC_ROUTE_LAYOUTS.account,
  PUBLIC_ROUTE_LAYOUTS['account-enrollments'],
  PUBLIC_ROUTE_LAYOUTS.onboarding,
  PUBLIC_ROUTE_LAYOUTS['creator-apply'],
];

export const PUBLIC_BOTTOM_NAV = {
  guest: [
    { label: 'Calendar', routeHint: 'active geography calendar' },
    { label: 'Updates', routeHint: 'active geography updates' },
    { label: 'Locations', routeHint: 'active geography locations' },
    { label: 'Creators', routeHint: 'active geography creators' },
    { label: 'Classes', routeHint: 'active geography classes' },
  ],
  authenticated: [
    { label: 'Calendar', routeHint: 'active geography calendar' },
    { label: 'Updates', routeHint: 'active geography updates' },
    { label: 'Locations', routeHint: 'active geography locations' },
    { label: 'Creators', routeHint: 'active geography creators' },
    { label: 'Classes', routeHint: 'active geography classes' },
  ],
} as const;

export const DESKTOP_NAV_MODEL = {
  primary: ['Geography switcher', 'Classes', 'Creators', 'Search', 'Calendar'],
  utility: ['Creator Dashboard', 'Admin', 'Account', 'Sign in / Sign out', 'Create with Kalovate'],
} as const;

export const BROWSE_STATE_OWNERSHIP = [
  {
    param: 'region',
    owner: 'URL',
    notes: 'Used by global search and shareable discovery links outside geography path routes.',
  },
  {
    param: 'locality',
    owner: 'URL',
    notes: 'Optional narrowing below the active region for search and restore links.',
  },
  {
    param: 'date',
    owner: 'URL',
    notes: 'Canonical calendar filter so regional agendas are deep-linkable.',
  },
  {
    param: 'q',
    owner: 'URL',
    notes: 'User-entered search query across classes and creators.',
  },
  {
    param: 'category',
    owner: 'URL',
    notes: 'Single discovery taxonomy filter.',
  },
  {
    param: 'sort',
    owner: 'URL',
    notes: 'Primary list ordering for browse and search routes.',
  },
  {
    param: 'view',
    owner: 'URL',
    notes: 'List or grid surface mode for larger screens.',
  },
  {
    param: 'scroll',
    owner: 'sessionStorage',
    notes: 'Ephemeral return-to-results restoration keyed by path plus query hash.',
  },
] as const;

export const GEOGRAPHY_FALLBACK_MATRIX = [
  {
    scenario: 'Locality page, no classes on selected date',
    fallback: 'Show nearest upcoming local dates and keep the selected date visible in context.',
  },
  {
    scenario: 'Locality page, filters return zero results',
    fallback:
      'Relax one filter at a time, explain the broadened result set, and preserve the original filter chips.',
  },
  {
    scenario: 'Region with low creator supply',
    fallback: 'Promote featured region-wide creators plus a request-a-class CTA.',
  },
  {
    scenario: 'No local classes at all',
    fallback: 'Surface online-region classes relevant to the market and invite newsletter signup.',
  },
] as const;

export const PERFORMANCE_BUDGETS = [
  'LCP under 2.5s on landing, browse, and class detail routes.',
  'CLS under 0.1 on all public routes.',
  'INP under 200ms for interactive browse controls.',
] as const;

export const REQUIRED_QA_VIEWPORT_WIDTHS = [320, 375, 390, 430, 768, 834, 1024, 1280] as const;

export const BROWSER_SUPPORT_MATRIX = [
  'iPhone Safari (latest)',
  'Chrome on Android (latest)',
  'iPad Safari in portrait and landscape (latest)',
  'Desktop Chrome',
  'Desktop Safari',
] as const;

export const PHASE4_QA_CANONICAL_ROUTE_KEYS = [
  'root',
  'region-home',
  'locality-home',
  'classes',
  'class-detail',
  'locations',
  'location-detail',
  'creators',
  'creator-detail',
  'search',
  'checkout',
  'checkout-confirmation',
  'account',
  'account-enrollments',
  'faq',
] as const satisfies readonly PublicRouteKey[];

export const ROOT_ROUTE_DISCOVERY_BEHAVIOR =
  'Returning visitors route directly into their saved region or locality. First-time visitors land on a geography discovery screen with region cards, local proof points, and one clear market-selection action.';

export const GEOGRAPHY_SWITCHER_BEHAVIOR =
  'The switcher is available in both mobile and desktop chrome, keeps the current geography visible, persists the new selection immediately, and navigates to the matching region or locality route without dropping browse context when a destination equivalent exists.';
